import React, { useMemo, useState } from 'react';
import type { AxiosError } from 'axios';
import { AlertCircle, CheckCircle2, MoreHorizontal, Network, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import { useDivisions } from '../../hooks/useDivisions';
import { useCredentials } from '../../hooks/useCredentials';
import {
  useTransportDefinitions,
  useCreateTransportDefinition,
  useUpdateTransportDefinition,
  useDeleteTransportDefinition,
} from '../../hooks/useTransportDefinitions';
import type {
  TransportDefinition,
  CreateTransportDefinitionRequest,
  UpdateTransportDefinitionRequest,
} from '../../hooks/useTransportDefinitions';
import ConfirmationModal from '../../components/ConfirmationModal';
import CredentialSelect from '../../components/CredentialSelect';
import { SSH_CREDENTIAL_TYPES } from '../../components/credentialTypes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TRANSPORT_TYPES = [
  { value: 'ssh_tunnel', label: 'SSH Tunnel', defaultPort: 22 },
  { value: 'http_proxy', label: 'HTTP Proxy', defaultPort: 8080 },
  { value: 'socks5', label: 'SOCKS5', defaultPort: 1080 },
  { value: 'telnet', label: 'Telnet', defaultPort: 23 },
] as const;

const NO_SELECTION = '__none__';

interface ApiErrorResponse {
  error: string;
  description?: string;
}

function getTransportTypeLabel(value: string): string {
  return TRANSPORT_TYPES.find((t) => t.value === value)?.label ?? value;
}

function getDefaultPort(transportType: string): number {
  return TRANSPORT_TYPES.find((t) => t.value === transportType)?.defaultPort ?? 22;
}

export default function TransportDefinitions() {
  const { user } = useAuth();

  const {
    data: transports,
    isLoading: isLoadingTransports,
    isError: isErrorTransports,
  } = useTransportDefinitions(user?.organizationId);
  const { data: regions, isLoading: isLoadingRegions } = useRegions(user?.organizationId);
  const { data: divisions, isLoading: isLoadingDivisions } = useDivisions(user?.organizationId);
  const { data: credentialsData } = useCredentials(user?.organizationId);

  const credentials = credentialsData ?? [];
  const regionsArray = useMemo(() => regions ?? [], [regions]);
  const divisionsArray = divisions ?? [];
  const transportsList = transports ?? [];

  const createMutation = useCreateTransportDefinition();
  const updateMutation = useUpdateTransportDefinition();
  const deleteMutation = useDeleteTransportDefinition();

  const [isModalActive, setIsModalActive] = useState(false);
  const [editingTransport, setEditingTransport] = useState<TransportDefinition | null>(null);

  const [formName, setFormName] = useState('');
  const [formTransportType, setFormTransportType] = useState('ssh_tunnel');
  const [formHost, setFormHost] = useState('');
  const [formPort, setFormPort] = useState(22);
  const [formCredentialId, setFormCredentialId] = useState(NO_SELECTION);
  const [formParentTransportId, setFormParentTransportId] = useState(NO_SELECTION);
  const [formRegionId, setFormRegionId] = useState('');
  const [formDivisionId, setFormDivisionId] = useState(NO_SELECTION);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const [deleteConfirmActive, setDeleteConfirmActive] = useState(false);
  const [transportToDelete, setTransportToDelete] = useState<TransportDefinition | null>(null);

  if (regionsArray.length === 1 && !formRegionId && !editingTransport) {
    setFormRegionId(regionsArray[0].id);
  }

  const resetForm = () => {
    setFormName('');
    setFormTransportType('ssh_tunnel');
    setFormHost('');
    setFormPort(22);
    setFormCredentialId(NO_SELECTION);
    setFormParentTransportId(NO_SELECTION);
    setFormRegionId(regionsArray.length === 1 ? regionsArray[0].id : '');
    setFormDivisionId(NO_SELECTION);
    setSubmitError(null);
  };

  const openCreateModal = () => {
    setEditingTransport(null);
    resetForm();
    setIsModalActive(true);
  };

  const openEditModal = (transport: TransportDefinition) => {
    setEditingTransport(transport);
    setFormName(transport.name);
    setFormTransportType(transport.transportType);
    setFormHost(transport.host);
    setFormPort(transport.port);
    setFormCredentialId(transport.credentialId ?? NO_SELECTION);
    setFormParentTransportId(transport.parentTransportId ?? NO_SELECTION);
    setFormRegionId(transport.regionId);
    setFormDivisionId(transport.divisionId ?? NO_SELECTION);
    setSubmitError(null);
    setIsModalActive(true);
  };

  const closeModal = () => {
    setIsModalActive(false);
    setEditingTransport(null);
    resetForm();
  };

  const handleTransportTypeChange = (newType: string) => {
    setFormTransportType(newType);
    const oldDefault = getDefaultPort(formTransportType);
    if (formPort === oldDefault) {
      setFormPort(getDefaultPort(newType));
    }
  };

  const credentialIdOrNull = formCredentialId === NO_SELECTION ? null : formCredentialId;
  const parentIdOrNull = formParentTransportId === NO_SELECTION ? null : formParentTransportId;
  const divisionIdOrNull = formDivisionId === NO_SELECTION ? null : formDivisionId;

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!formName.trim()) { setSubmitError('Name is required.'); return; }
    if (!formHost.trim()) { setSubmitError('Host is required.'); return; }
    if (formPort < 1 || formPort > 65535) { setSubmitError('Port must be between 1 and 65535.'); return; }
    if (!formRegionId) { setSubmitError('Region is required.'); return; }

    if (editingTransport) {
      const body: { id: string } & UpdateTransportDefinitionRequest = {
        id: editingTransport.id,
        name: formName,
        transportType: formTransportType,
        host: formHost,
        port: formPort,
        credentialId: credentialIdOrNull,
        parentTransportId: parentIdOrNull,
        regionId: formRegionId,
        divisionId: divisionIdOrNull,
      };
      updateMutation.mutate(body, {
        onSuccess: () => {
          closeModal();
          setSubmitSuccess('Transport definition updated successfully.');
        },
        onError: (err: Error) => {
          const axiosErr = err as AxiosError<ApiErrorResponse>;
          setSubmitError(
            axiosErr.response?.data.description ??
              axiosErr.response?.data.error ??
              'Failed to update transport definition.',
          );
        },
      });
    } else {
      const body: CreateTransportDefinitionRequest = {
        name: formName,
        transportType: formTransportType,
        host: formHost,
        port: formPort,
        credentialId: credentialIdOrNull,
        parentTransportId: parentIdOrNull,
        regionId: formRegionId,
        divisionId: divisionIdOrNull,
      };
      createMutation.mutate(body, {
        onSuccess: () => {
          closeModal();
          setSubmitSuccess('Transport definition created successfully.');
        },
        onError: (err: Error) => {
          const axiosErr = err as AxiosError<ApiErrorResponse>;
          setSubmitError(
            axiosErr.response?.data.description ??
              axiosErr.response?.data.error ??
              'Failed to create transport definition.',
          );
        },
      });
    }
  };

  const handleDeleteClick = (transport: TransportDefinition) => {
    setTransportToDelete(transport);
    setDeleteConfirmActive(true);
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleConfirmDelete = () => {
    if (!transportToDelete) return;
    deleteMutation.mutate(transportToDelete.id, {
      onSuccess: () => {
        setDeleteConfirmActive(false);
        setTransportToDelete(null);
        setSubmitSuccess('Transport definition deleted successfully.');
      },
      onError: (err: Error) => {
        const axiosErr = err as AxiosError<ApiErrorResponse>;
        setDeleteConfirmActive(false);
        setTransportToDelete(null);
        setSubmitError(
          axiosErr.response?.data.description ??
            axiosErr.response?.data.error ??
            'Failed to delete transport definition.',
        );
      },
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmActive(false);
    setTransportToDelete(null);
  };

  const filteredParentTransports = transportsList.filter((t) => {
    if (editingTransport && t.id === editingTransport.id) return false;
    if (formRegionId && t.regionId !== formRegionId) return false;
    return true;
  });

  const regionName = (id: string) =>
    regionsArray.find((r) => r.id === id)?.name ?? id;
  const divisionName = (id: string | null) => {
    if (!id) return '—';
    return divisionsArray.find((d) => d.id === id)?.name ?? id;
  };
  const credentialName = (id: string | null) => {
    if (!id) return '—';
    return credentials.find((c) => c.id === id)?.name ?? id;
  };
  const parentTransportName = (id: string | null) => {
    if (!id) return '—';
    return transportsList.find((t) => t.id === id)?.name ?? id;
  };

  if (isLoadingTransports || isLoadingRegions || isLoadingDivisions) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">Loading transport definitions…</p>
      </PageContainer>
    );
  }

  if (isErrorTransports) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load transport definitions.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  const columns: readonly ResponsiveTableColumn<TransportDefinition>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (t) => <span className="font-medium">{t.name}</span>,
    },
    {
      key: 'type',
      label: 'Type',
      render: (t) => (
        <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
          {getTransportTypeLabel(t.transportType)}
        </Badge>
      ),
    },
    {
      key: 'host',
      label: 'Host:Port',
      render: (t) => (
        <code className="font-mono text-xs">
          {t.host}:{t.port}
        </code>
      ),
    },
    {
      key: 'credential',
      label: 'Credential',
      hideAt: 'mobile',
      render: (t) => (
        <span className="text-xs text-muted-foreground">
          {credentialName(t.credentialId)}
        </span>
      ),
    },
    {
      key: 'parent',
      label: 'Parent transport',
      hideAt: 'mobile',
      render: (t) => (
        <span className="text-xs text-muted-foreground">
          {parentTransportName(t.parentTransportId)}
        </span>
      ),
    },
    {
      key: 'region',
      label: 'Region',
      hideAt: 'mobile',
      render: (t) => (
        <span className="text-xs text-muted-foreground">{regionName(t.regionId)}</span>
      ),
    },
    {
      key: 'division',
      label: 'Division',
      hideAt: 'mobile',
      render: (t) => (
        <span className="text-xs text-muted-foreground">{divisionName(t.divisionId)}</span>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Transport definitions"
        primary={
          <Button onClick={openCreateModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Create transport
          </Button>
        }
      />

      {submitSuccess ? (
        <Alert className="mb-4 border-sev-4/40 bg-sev-4/10 relative">
          <CheckCircle2 className="h-4 w-4 text-sev-4" />
          <AlertDescription>{submitSuccess}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitSuccess(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {submitError && !isModalActive ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {transportsList.length === 0 ? (
        <EmptyState
          variant="no-data"
          icon={Network}
          title="No transport definitions"
          description="Create a transport to define how the gateway reaches a network (SSH bastion, HTTP proxy, SOCKS5, telnet)."
          action={
            <Button onClick={openCreateModal} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Create transport
            </Button>
          }
        />
      ) : (
        <ResponsiveTable<TransportDefinition>
          columns={columns}
          rows={transportsList}
          rowKey={(r) => r.id}
          rowActions={(transport) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); }}
                  aria-label={`Actions for ${transport.name}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
                <DropdownMenuItem
                  onClick={() => { openEditModal(transport); }}
                  className="gap-2"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { handleDeleteClick(transport); }}
                  className="gap-2 text-sev-1 focus:text-sev-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
      )}

      <AppSheet
        open={isModalActive}
        onOpenChange={(open) => { if (!open) closeModal(); }}
        title={editingTransport ? 'Edit transport definition' : 'Create transport definition'}
        size="md"
      >
        <AppSheet.Body>
          {submitError && isModalActive ? (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <form id="transport-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="td-name">Name</Label>
              <Input
                id="td-name"
                type="text"
                placeholder="e.g., DC1 SSH Bastion"
                value={formName}
                onChange={(e) => { setFormName(e.target.value); }}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-type">Transport type</Label>
              <Select
                value={formTransportType}
                onValueChange={handleTransportTypeChange}
                required
              >
                <SelectTrigger id="td-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSPORT_TYPES.map((tt) => (
                    <SelectItem key={tt.value} value={tt.value}>
                      {tt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="td-host">Host</Label>
                <Input
                  id="td-host"
                  type="text"
                  placeholder="e.g., 10.0.1.5 or bastion.example.com"
                  value={formHost}
                  onChange={(e) => { setFormHost(e.target.value); }}
                  required
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="td-port">Port</Label>
                <Input
                  id="td-port"
                  type="number"
                  min={1}
                  max={65535}
                  value={formPort}
                  onChange={(e) => {
                    setFormPort(parseInt(e.target.value, 10) || 0);
                  }}
                  required
                  className="font-mono"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-region">Region</Label>
              <Select
                value={formRegionId}
                onValueChange={(v) => {
                  setFormRegionId(v);
                  setFormCredentialId(NO_SELECTION);
                  setFormParentTransportId(NO_SELECTION);
                }}
                required
              >
                <SelectTrigger id="td-region">
                  <SelectValue placeholder="Select a region" />
                </SelectTrigger>
                <SelectContent>
                  {regionsArray.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-division">Division (optional)</Label>
              <Select value={formDivisionId} onValueChange={setFormDivisionId}>
                <SelectTrigger id="td-division">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SELECTION}>None (organization-wide)</SelectItem>
                  {divisionsArray.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-credential">Credential (optional)</Label>
              {/* Only an SSH tunnel logs in to its bastion; the other kinds use none. */}
              <CredentialSelect
                id="td-credential"
                value={credentialIdOrNull}
                onChange={(id) => { setFormCredentialId(id ?? NO_SELECTION); }}
                regionId={formRegionId}
                types={SSH_CREDENTIAL_TYPES}
                optional
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-parent">Parent transport (optional)</Label>
              <Select
                value={formParentTransportId}
                onValueChange={setFormParentTransportId}
              >
                <SelectTrigger id="td-parent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SELECTION}>None (direct connection)</SelectItem>
                  {filteredParentTransports.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.host}:{t.port})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formRegionId && filteredParentTransports.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No other transports available in this region.
                </p>
              ) : null}
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={closeModal}
            disabled={isMutating}
          >
            Cancel
          </Button>
          <Button type="submit" form="transport-form" disabled={isMutating}>
            {isMutating
              ? 'Saving…'
              : editingTransport
                ? 'Save changes'
                : 'Create transport'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={deleteConfirmActive}
        title="Delete transport definition"
        message={`Are you sure you want to delete "${transportToDelete?.name ?? 'Unknown'}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteMutation.isPending}
        isDanger={true}
      />
    </PageContainer>
  );
}
